import React from 'react';
import {defaultCountries, parseCountry} from 'react-international-phone';
import {getCountriesService, getStatesService} from '../services/geo.service';
// Deprecated local lists — used only until the API answers, or when it cannot be reached.
import {countries as LEGACY_COUNTRIES} from './countries';
import {getStatesByCountry as legacyStatesByCountry} from './dq_states_data';

/*
 * Countries, states and phone-picker countries, from the API's geo master
 * (GET /v1/meta/countries…) so every DQ app shows the same list.
 *
 * Everything here is synchronous so existing call sites keep their shape:
 *   countries            [{ code, label, name, dial_code, has_states }]
 *   getStatesByCountry   [{ code, name, … }]
 * Before the API answers they return the deprecated local lists; components that call
 * `useGeo()` re-render once the API's lists arrive. Plain helpers (Excel templates,
 * validation) read whatever is loaded at the time they run.
 *
 * Save codes, never names: country = ISO 3166-1 alpha-2, state = ISO 3166-2.
 */

const toEntry = (c) => ({code: c.code, label: c.name, name: c.name, dial_code: c.dial_code || null, has_states: !!c.has_states});

let countries = LEGACY_COUNTRIES.map((c) => toEntry({
    code: c.code, name: c.label, dial_code: null, has_states: legacyStatesByCountry(c.code).length > 0,
}));
let byCode = new Map(countries.map((c) => [c.code, c]));
// Names older records may hold instead of a code (the sign-up saved the label until Oct 2026).
const LEGACY_LABEL_TO_CODE = new Map(LEGACY_COUNTRIES.map((c) => [c.label.toLowerCase(), c.code]));

let countriesStatus = 'idle';   // idle | loading | loaded | failed
let countriesPromise = null;
const statesByCountry = new Map(); // code → states from the API
const statesLoading = new Set();

let revision = 0;
let phoneCountriesCache = null;
const listeners = new Set();
const notify = () => {
    revision += 1;
    phoneCountriesCache = null;
    listeners.forEach((l) => l(revision));
};

const upper = (v) => String(v || '').trim().toUpperCase();

/** Fetches the country list once per page load. Safe to call repeatedly. */
export const loadCountries = () => {
    if (countriesPromise) return countriesPromise;
    countriesStatus = 'loading';
    countriesPromise = getCountriesService()
        .then((res) => {
            const list = res && res.data && res.data.countries;
            if (!Array.isArray(list) || !list.length) { countriesStatus = 'failed'; return; }
            countries = list.map(toEntry);
            byCode = new Map(countries.map((c) => [c.code, c]));
            countriesStatus = 'loaded';
            notify();
        })
        .catch(() => { countriesStatus = 'failed'; });
    return countriesPromise;
};

const ensureStates = (cc) => {
    if (statesByCountry.has(cc) || statesLoading.has(cc)) return;
    statesLoading.add(cc);
    getStatesService(cc)
        .then((res) => {
            const list = res && res.data && res.data.states;
            statesByCountry.set(cc, Array.isArray(list) ? list : legacyStatesByCountry(cc));
            notify();
        })
        // Not retried on every render: keep the local list for this page load.
        .catch(() => statesByCountry.set(cc, legacyStatesByCountry(cc)))
        .finally(() => statesLoading.delete(cc));
};

/** The current country list — the API's once loaded, the deprecated local one before. */
export const getCountries = () => countries;

/** A country's states. Starts loading them from the API on first use. */
export const getStatesByCountry = (countryCode) => {
    const cc = upper(countryCode);
    if (!cc) return [];
    if (statesByCountry.has(cc)) return statesByCountry.get(cc);
    const country = byCode.get(cc);
    if (!country) return countriesStatus === 'loaded' ? [] : legacyStatesByCountry(cc);
    if (countriesStatus === 'loaded' && !country.has_states) return [];
    ensureStates(cc);
    return legacyStatesByCountry(cc);
};

/** Display name for a code; '' when unknown. */
export const getCountryLabel = (code) => (byCode.get(upper(code)) || {}).label || '';

/** Display name for a state code ('KR-11'); '' when unknown. */
export const getStateLabel = (stateCode) => {
    const sc = upper(stateCode);
    const hit = getStatesByCountry(sc.split('-')[0]).find((s) => s.code === sc);
    return hit ? hit.name : '';
};

/** The country for a stored value that may be a code or (older records) a name. */
export const findCountry = (codeOrName) => {
    const raw = String(codeOrName || '').trim();
    if (!raw) return undefined;
    const direct = byCode.get(raw.toUpperCase());
    if (direct) return direct;
    const lower = raw.toLowerCase();
    return countries.find((c) => c.label.toLowerCase() === lower)
        || byCode.get(LEGACY_LABEL_TO_CODE.get(lower));
};

export const isKnownCountryCode = (code) => byCode.has(upper(code));

/** Dialling code for an ISO code, e.g. 'sg' → '+65'; '' when unknown. */
export const getCountryDialCode = (iso2) => (byCode.get(upper(iso2)) || {}).dial_code || '';

/**
 * Countries for react-international-phone's `PhoneInput countries` prop: the library's
 * own entries (dial codes, formats, area codes) under the master's names, plus the
 * master's countries the library lacks (Bermuda, Jersey, Guam, …). Those carry no area
 * codes, so they get priority 99 — typing +1 or +44 still picks the US or the UK; they
 * are chosen from the dropdown.
 */
export const getPhoneCountries = () => {
    if (phoneCountriesCache) return phoneCountriesCache;
    const inLibrary = new Set();
    const list = defaultCountries.map((c) => {
        const iso2 = parseCountry(c).iso2.toUpperCase();
        inLibrary.add(iso2);
        const entry = byCode.get(iso2);
        return entry ? [entry.label, ...c.slice(1)] : c;
    });
    countries.forEach((c) => {
        if (!inLibrary.has(c.code) && c.dial_code) list.push([c.label, c.code.toLowerCase(), c.dial_code.replace('+', ''), '', 99]);
    });
    phoneCountriesCache = list.sort((a, b) => a[0].localeCompare(b[0]));
    return phoneCountriesCache;
};

/**
 * Subscribes a component to the geo lists: loads them on mount and re-renders when the
 * API's countries, or a country's states, arrive. Read the lists through the returned
 * object (or the getters above) during render.
 */
export const useGeo = () => {
    const [, setRevision] = React.useState(revision);
    React.useEffect(() => {
        listeners.add(setRevision);
        loadCountries();
        setRevision(revision);
        return () => { listeners.delete(setRevision); };
    }, []);
    return {
        countries,
        getStatesByCountry,
        getCountryLabel,
        getStateLabel,
        findCountry,
        phoneCountries: getPhoneCountries(),
    };
};
